import React, { useState, useMemo, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react'
import { TextAreaHLPure, InputHLPure, DataPickerHLPure, InteractiveDialog, DropDownBtnHLPure, Traslates, ButtonHLPure, DialogSystemGLPure, ButtonAddHLPure } from '../../pures'
import { ChipList, Chip } from '@progress/kendo-react-buttons'
import CheckBoxHLPure from '../checkBoxHL/checkBoxHL.pure'
import UserService from '../../../services/user.service'
import { useTranslation } from 'react-i18next'
import './documentDetailDataPure.css'

const MAX_TEXT_CHARS = 350

const isValidDate = (d) => d instanceof Date && !isNaN(d)

function startOfDay(date) {
  if (!isValidDate(date)) return null
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function parseDateStrict(value) {
  if (!value) return null
  if (value instanceof Date) return isValidDate(value) ? value : null
  if (typeof value === 'number') {
    const d = new Date(value)
    return isValidDate(d) ? d : null
  }
  if (typeof value !== 'string') return null

  const s = value.trim()
  if (!s) return null

  const native = new Date(s)
  if (isValidDate(native)) return native

  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]))
    return isValidDate(d) ? d : null
  }

  m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/)
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]))
    return isValidDate(d) ? d : null
  }

  m = s.match(/^(\d{1,2})\s+([A-Za-zÁÉÍÓÚáéíóúÑñ.]{3,})\.?\s+(\d{4})$/)
  if (m) {
    const months = {
      ene: 0, enero: 0,
      feb: 1, febrero: 1,
      mar: 2, marzo: 2,
      abr: 3, abril: 3,
      may: 4, mayo: 4,
      jun: 5, junio: 5,
      jul: 6, julio: 6,
      ago: 7, agosto: 7,
      sep: 8, sept: 8, septiembre: 8,
      oct: 9, octubre: 9,
      nov: 10, noviembre: 10,
      dic: 11, diciembre: 11
    }
    const day = Number(m[1])
    const raw = m[2].toLowerCase().replace(/\.$/, '')
    const key = raw.length > 3 ? raw : raw.substring(0, 3)
    const month = months[key]
    const year = Number(m[3])
    if (typeof month === 'number') {
      const d = new Date(year, month, day)
      return isValidDate(d) ? d : null
    }
  }

  return null
}

// Item simple
function DetailItem({ field, fieldValue }) {
  return (
    <div className='detail-item-main-div'>
      <p className='detail-item-field'>{field}</p>
      <p className='detail-item-value detail-item-value-wrapped'>{fieldValue}</p>
    </div>
  )
}

// Item truncado con “Ver más”
function TruncatedDetailItem({ field, value, maxChars, onSeeMore, extraClass }) {
  const needsTruncate = typeof value === 'string' && value.length > maxChars
  const display = needsTruncate ? value.slice(0, maxChars) + '… ' : value

  const { t } = useTranslation()

  return (
    <div className='detail-item-main-div'>
      <p className='detail-item-field'>{field}</p>
      <p className={`detail-item-value ${extraClass || ''}`}>
        {display}
        {needsTruncate && (
          <button type='button' className='see-more-link' onClick={e => { e.stopPropagation(); onSeeMore(); }}>
            {t('digitalVaultDocumentView.more.view')}
          </button>
        )}
      </p>
    </div>
  )
}

// Barra de progreso
function ProgressBar({ percent = 0, expired = false }) {
  return (
    <div className='expires-bar'>
      <div className={`expires-fill ${expired ? 'expired' : ''}`} style={{ width: `${percent}%` }} />
    </div>
  )
}

const DocumentDetailDataPure = forwardRef(function DocumentDetailDataPure({ fieldValueArray, documentData, isEditing = false, isInDeleteFlow }, ref) {
  const { t } = useTranslation()
  const [openModal, setOpenModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [showWarn, setShowWarn] = useState(false)
  const [warnMsg, setWarnMsg] = useState('')

  const userService = useMemo(() => new UserService(process.env.REACT_APP_MIFIRMA_API), [])

  // --- Estados
  const [nameValue, setNameValue] = useState(fieldValueArray[0]?.value || '')
  const FORMAT_DATE = 'dd/MM/yyyy'
  const [dateStart, setDateStart] = useState(() => parseDateStrict(fieldValueArray[1]?.value))
  const [dateEnd, setDateEnd] = useState(() => parseDateStrict(fieldValueArray[2]?.value))
  const [notifyExpiration, setNotifyExpiration] = useState(fieldValueArray[9]?.value || false)
  const [descriptionValue, setDescriptionValue] = useState(fieldValueArray[6]?.value || '')
  const [confidential, setConfidential] = useState(fieldValueArray[5]?.value || false)
  const [tempDescription, setTempDescription] = useState(descriptionValue)
  const descriptionField = fieldValueArray[6]?.field || 'Descripción'

  const docId = documentData?.id
  const docUserId = documentData?.userId

  const [diffDays, setDiffDays] = useState(documentData?.differenceDays || 0)
  const [percentDays, setPercentDays] = useState(
    documentData?.percentageDays ? parseFloat(documentData.percentageDays) : 0
  )

  const aiSummaryItem = useMemo(() => {
    return fieldValueArray.find(it =>
      typeof it?.field === 'string' &&
      (it.field.toLowerCase().includes('resumen ia') ||
        it.field.toLowerCase().includes('summary'))
    )
  }, [fieldValueArray])

  const backendSummary = fieldValueArray[8]?.value || ''
  const summaryValue = aiSummaryItem?.value || backendSummary
  const hasSummary = summaryValue && summaryValue.trim() !== ''
  const [hideSummary, setHideSummary] = useState(false)
  const showSummary = hasSummary && !hideSummary

  const [tags, setTags] = useState(Array.isArray(fieldValueArray[7]?.value) ? fieldValueArray[7].value : [])
  const [inputTag, setInputTag] = useState('')

  const [uploadData, setUploadData] = useState({
    tags: [],
    ownerId: documentData?.ownerId || null
  })

  const tagsEditRef = useRef(null)
  const nameEditRef = useRef(null)
  const descriptionEditRef = useRef(null)
  const ownerEditRef = useRef(null)

  const [users, setUsers] = useState([])
  const hasFinishDate = isValidDate(dateEnd)

  const minFinishDate = useMemo(() => {
    const today0 = startOfDay(new Date())
    const start0 = startOfDay(dateStart)
    if (start0 && start0 > today0) return start0
    return today0
  }, [dateStart])

  useEffect(() => {
    const fetchUsers = async () => {
      if (!documentData?.id) return
      try {
        const token = JSON.parse(localStorage.getItem('token'))?.idToken
        const documentId = documentData?.id
        const response = await userService.getOwnersDocument(documentId, token)
        const usersData = (response && response.data) || []
        setUsers(usersData)

        if (documentData?.userId) {
          const exists = usersData.find(u => String(u.id) === String(documentData.userId))
          if (exists) {
            setUploadData(prev => ({ ...prev, ownerId: exists.id }))
          }
        }
      } catch (error) {
        console.error(`Error:`, error)
      }
    }
    fetchUsers()
  }, [documentData, userService])

  useEffect(() => {
    setDiffDays(documentData?.differenceDays || 0)
    setPercentDays(documentData?.percentageDays ? parseFloat(documentData.percentageDays) : 0)
  }, [documentData])

  useEffect(() => {
    if (documentData?.userId) {
      setUploadData(prev => ({ ...prev, ownerId: documentData.userId }))
    }
  }, [documentData])

  useEffect(() => {
    const end0 = startOfDay(dateEnd)
    const min0 = startOfDay(minFinishDate)
    if (end0 && min0 && end0 < min0) {
      setDateEnd(minFinishDate)
      if (!isEditing) {
        (async () => {
          try {
            const token = JSON.parse(localStorage.getItem('token'))?.idToken
            const corrected = min0.toISOString().split('T')[0]
            const res = await userService.updateDocument({ documentId: docId, finishDate: corrected }, token)
            const doc = (res && (res.data || res)) || null
            if (doc) {
              const finish = doc.validation || doc.finishDate || doc.finish_date
              if (finish) setDateEnd(parseDateStrict(finish) || minFinishDate)
              if (typeof doc.differenceDays === 'number') setDiffDays(doc.differenceDays)
              if (doc.percentageDays !== undefined) setPercentDays(parseFloat(String(doc.percentageDays)))
            }
          } catch { }
        })()
      }
    }
  }, [minFinishDate, dateEnd, isEditing, docId, userService])

  const handleSaveField = useCallback(async (field, value) => {
    try {
      const token = JSON.parse(localStorage.getItem('token'))?.idToken

      const formatDate = (d) => {
        return d instanceof Date && !isNaN(d) ? d.toISOString().split('T')[0] : null
      }

      const finalOwnerId = uploadData.ownerId || docUserId
      const normalizeEmpty = (v) => {
        if (typeof v === 'string' && v.trim() === '') return ''
        if (v === undefined || v === null) return ''
        return v
      }

      const today0 = startOfDay(new Date())
      const startCandidate = field === 'startDate' ? parseDateStrict(value) || dateStart : dateStart
      const endCandidate = field === 'finishDate' ? parseDateStrict(value) || dateEnd : dateEnd
      const start0 = startOfDay(startCandidate)
      const min0 = (start0 && start0 > today0) ? start0 : today0
      const end0 = startOfDay(endCandidate)
      const safeEnd = (end0 && min0 && end0 < min0) ? min0 : end0

      const basePayload = {
        documentId: docId,
        name: nameValue,
        ownerId: finalOwnerId,
        description: normalizeEmpty(descriptionValue),
        startDate: formatDate(dateStart),
        finishDate: formatDate(safeEnd),
        confidential: confidential,
        notify: notifyExpiration,
      }

      const extraField = (() => {
        if (field === 'description') return { [field]: normalizeEmpty(value) }
        if (field === 'finishDate') return { [field]: formatDate(safeEnd) }
        if (field === 'startDate') return { [field]: formatDate(startCandidate) }
        return { [field]: value }
      })()

      const payload = { ...basePayload, ...extraField }

      const res = await userService.updateDocument(payload, token)
      const doc = (res && (res.data || res)) || null
      if (doc) {
        const start = doc.startDate || doc.start_date || doc.initialDate
        const finish = doc.validation || doc.finishDate || doc.finish_date
        if (start) setDateStart(parseDateStrict(start) || dateStart)
        if (finish) setDateEnd(parseDateStrict(finish) || dateEnd)
        if (typeof doc.differenceDays === 'number') setDiffDays(doc.differenceDays)
        if (doc.percentageDays !== undefined) setPercentDays(parseFloat(String(doc.percentageDays)))
      }
    } catch (err) {
      console.error(`Error:`, err)
    }
  }, [
    userService,
    docId,
    docUserId,
    nameValue,
    descriptionValue,
    dateStart,
    dateEnd,
    confidential,
    notifyExpiration,
    uploadData.ownerId
  ])

  const handleInputTag = (e) => setInputTag(e.target.value)

  const onBlurInputTag = () => {
    if (inputTag.trim() === '') {
      setEditing(null)
      return
    }
    const newTag = { text: inputTag.trim(), value: tags.length }
    const newTagExists = tags.some(t => (typeof t === 'string' ? t : (t?.text || '')) === newTag.text)
    if (newTagExists) {
      setInputTag('')
      setEditing(null)
      return
    }
    const updated = [...tags, newTag]
    setTags(updated)
    setInputTag('')
    if (!isEditing) {
      handleSaveField('tags', updated.map(tag => tag.text))
    }
    setEditing(null)
  }

  const handleDeleteChipTag = (e) => {
    let copyTags = [...tags]
    let tagDelete = e.target.props.dataItem
    let tagsFilter = copyTags.filter((tag) => tag.value !== tagDelete.value)
    setTags(tagsFilter)
    setUploadData(prev => ({
      ...prev,
      tags: tagsFilter.map(tag => tag.text)
    }))
    if (!isEditing) {
      handleSaveField('tags', tagsFilter.map(tag => tag.text))
    }
  }

  const handleCloseModal = () => setOpenModal(false)
  const toggleWarning = () => setShowWarn(prev => !prev)

  useEffect(() => {
    if (openModal) {
      setTempDescription(descriptionValue)
    }
  }, [openModal, descriptionValue])

  const handleSaveModal = () => {
    setDescriptionValue(tempDescription)
    if (!isEditing) {
      handleSaveField('description', tempDescription)
    }
    setOpenModal(false)
  }

  useEffect(() => {
    if (!editing || isEditing) return
    const handleClickOutside = (e) => {
      const refsMap = {
        name: nameEditRef,
        description: descriptionEditRef,
        tags: tagsEditRef,
        owner: ownerEditRef
      }
      const activeRef = refsMap[editing]
      if (activeRef?.current && !activeRef.current.contains(e.target)) {
        if (editing === 'tags') {
          const pending = (inputTag || '').trim()
          if (pending) {
            const exists = (Array.isArray(tags) ? tags : []).some(t => (typeof t === 'string' ? t : (t?.text || '')) === pending)
            let updated = tags
            if (!exists) {
              const nextVal = (Array.isArray(tags) ? tags : []).reduce((m, t) => {
                const v = (t && typeof t === 'object' && typeof t.value === 'number') ? t.value : -1
                return Math.max(m, v)
              }, -1) + 1
              updated = [...(Array.isArray(tags) ? tags : []), { text: pending, value: nextVal }]
              setTags(updated)
            }
            setInputTag('')
            handleSaveField('tags', updated.map(tag => tag.text))
          }
        } else if (editing === 'owner') {
          handleSaveField('ownerId', uploadData.ownerId)
        } else if (editing === 'name') {
          handleSaveField('name', nameValue)
        } else if (editing === 'description') {
          handleSaveField('description', descriptionValue)
        }
        setEditing(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editing, inputTag, tags, handleSaveField, nameValue, descriptionValue, dateStart, dateEnd, uploadData.ownerId, isEditing])

  useImperativeHandle(ref, () => ({
    saveAll: async () => {
      try {
        const token = JSON.parse(localStorage.getItem('token'))?.idToken
        const formatDate = (d) => (d instanceof Date && !isNaN(d) ? d.toISOString().split('T')[0] : null)
        const finalOwnerId = uploadData.ownerId || docUserId
        const tagsOut = (Array.isArray(tags) ? tags : []).map(t => typeof t === 'string' ? t : (t?.text || '')).filter(Boolean)
        const normalizeEmpty = (v) => {
          if (typeof v === 'string' && v.trim() === '') return ''
          if (v === undefined || v === null) return ''
          return v
        }
        const payload = {
          documentId: docId,
          name: nameValue,
          ownerId: finalOwnerId,
          description: normalizeEmpty(descriptionValue),
          startDate: formatDate(dateStart),
          finishDate: (() => {
            const today0 = startOfDay(new Date())
            const start0 = startOfDay(dateStart)
            const min0 = (start0 && start0 > today0) ? start0 : today0
            const end0 = startOfDay(dateEnd)
            const safeEnd = (end0 && min0 && end0 < min0) ? min0 : end0
            return formatDate(safeEnd)
          })(),
          confidential: confidential,
          notify: notifyExpiration,
          tags: tagsOut,
        }
        const res = await userService.updateDocument(payload, token)
        const doc = (res && (res.data || res)) || null
        if (doc) {
          const start = doc.startDate || doc.start_date || doc.initialDate
          const finish = doc.validation || doc.finishDate || doc.finish_date
          if (start) setDateStart(parseDateStrict(start) || dateStart)
          if (finish) setDateEnd(parseDateStrict(finish) || dateEnd)
          if (typeof doc.differenceDays === 'number') setDiffDays(doc.differenceDays)
          if (doc.percentageDays !== undefined) setPercentDays(parseFloat(String(doc.percentageDays)))
        }
        return true
      } catch (err) {
        return false
      }
    }
  }))

  const mergeAndSave = async () => {
    const desc = typeof descriptionValue === 'string' ? descriptionValue : (descriptionValue ?? '')
    const sum = typeof summaryValue === 'string' ? summaryValue : (summaryValue ?? '')
    const merged = [desc, sum].filter(Boolean).join('\n\n')
    if (merged.length > 1000) {
      const msg = t('digitalVaultDocumentView.excede.caracthers')
      setWarnMsg(msg)
      setShowWarn(true)
      return
    }

    setDescriptionValue(merged)
    setTempDescription(merged)
    setHideSummary(true)

    try {
      const token = JSON.parse(localStorage.getItem('token'))?.idToken
      const formatDate = (d) =>
        d instanceof Date && !isNaN(d) ? d.toISOString().split('T')[0] : null
      const finalOwnerId = uploadData.ownerId || docUserId
      const normalizeEmpty = (v) => (!v || !String(v).trim() ? '' : v)

      const payload = {
        documentId: docId,
        name: nameValue,
        ownerId: finalOwnerId,
        description: normalizeEmpty(merged),
        startDate: formatDate(dateStart),
        finishDate: (() => {
          const today0 = startOfDay(new Date())
          const start0 = startOfDay(dateStart)
          const min0 = (start0 && start0 > today0) ? start0 : today0
          const end0 = startOfDay(dateEnd)
          const safeEnd = (end0 && min0 && end0 < min0) ? min0 : end0
          return formatDate(safeEnd)
        })(),
        confidential,
        notify: notifyExpiration,
        summary: ''
      }

      const res = await userService.updateDocument(payload, token)
      const doc = (res && (res.data || res)) || null
      if (doc) {
        const start = doc.startDate || doc.start_date || doc.initialDate
        const finish = doc.validation || doc.finishDate || doc.finish_date
        if (start) setDateStart(parseDateStrict(start) || dateStart)
        if (finish) setDateEnd(parseDateStrict(finish) || dateEnd)
        if (typeof doc.differenceDays === 'number') setDiffDays(doc.differenceDays)
        if (doc.percentageDays !== undefined) setPercentDays(parseFloat(String(doc.percentageDays)))
      }
    } catch (err) {
    }
  }

  const onEnterTag = (e) => {
    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      e.preventDefault()
      if (inputTag.trim() !== '') {
        onBlurInputTag()
      }
    }
  }

  return (
    <>
      <div
        className={`documentDetailDataPure-main-div ${showSummary ? 'has-ai-summary' : 'no-ai-summary'}`}
      >
        {/* --- Columna 1 --- */}
        <div className='documentDetailDataPure-section-div documentDetailDataPure-first'>
          <div className='first-grid'>
            <div className='full-span'>
              {isEditing ? (
                <div ref={nameEditRef}>
                  <DetailItem field={fieldValueArray[0].field} />
                  <InputHLPure
                    typeInput='text'
                    inputActive={true}
                    valueInput={nameValue}
                    handleChangeInput={(e) => setNameValue(e.target.value)}
                    onBlur={() => { if (!isEditing) handleSaveField('name', nameValue) }}
                    characterLimit={128}
                  />
                </div>
              ) : (
                <div>
                  <DetailItem field={fieldValueArray[0].field} fieldValue={nameValue} />
                </div>
              )}
            </div>

            {/* Fecha inicio */}
            <div className='left-cell'>
              {isEditing ? (
                <div >
                  <DetailItem
                    field={fieldValueArray[1].field}
                  />
                  <Traslates>
                    <DataPickerHLPure
                      valueDate={dateStart}
                      handleChangeDate={(e) => {
                        const selected = e.value
                        setDateStart(selected)
                        const today0 = startOfDay(new Date())
                        const start0 = startOfDay(selected)
                        const newMin = (start0 && start0 > today0) ? start0 : today0
                        const end0 = startOfDay(dateEnd)
                        if (end0 && newMin && end0 < newMin) {
                          const corrected = new Date(newMin)
                          setDateEnd(corrected)
                          if (!isEditing) {
                            handleSaveField('finishDate', corrected.toISOString().split('T')[0])
                          }
                        }
                        if (!isEditing) {
                          handleSaveField('startDate', selected ? selected.toISOString().split('T')[0] : null)
                        }
                      }}
                      formatPicker={FORMAT_DATE}
                    />
                  </Traslates>

                </div>
              ) : (
                <div>
                  <DetailItem
                    field={fieldValueArray[1].field}
                    fieldValue={isValidDate(dateStart) ? dateStart.toLocaleDateString() : t('digitalVaultDocumentView.noDate')}
                  />
                </div>
              )}
            </div>

            {/* Progreso */}
            <div className='right-cell'>
              <div className='expires-wrapper'>
                {hasFinishDate ? (
                  <>
                    <span className='detail-item-field-expiration'>
                      {(() => {
                        if (diffDays === 0) return t('digitalVaultDocumentView.expires.today')
                        if (diffDays < 0) return t('digitalVaultDocumentView.expired.days', { days: Math.abs(diffDays) })
                        return t('digitalVaultDocumentView.expiresInDays', { days: diffDays })
                      })()}
                    </span>
                    <ProgressBar percent={percentDays} expired={diffDays < 0} />
                  </>
                ) : (
                  <div className='expires-placeholder' aria-hidden='true' />
                )}
              </div>
            </div>

            {/* Fecha fin */}
            <div className='left-cell'>
              {isEditing ? (
                <div>
                  <DetailItem field={fieldValueArray[2].field} />
                  <DataPickerHLPure
                    valueDate={dateEnd}
                    limitDate={minFinishDate}
                    handleChangeDate={(e) => {
                      const selected = e.value
                      if (selected) {
                        const sel0 = startOfDay(selected)
                        const min0 = startOfDay(minFinishDate)
                        if (sel0 && min0 && sel0 < min0) {
                          setDateEnd(minFinishDate)
                          if (!isEditing) {
                            handleSaveField('finishDate', min0.toISOString().split('T')[0])
                          }
                          return
                        }
                      }
                      setDateEnd(selected)
                      if (!isEditing) {
                        handleSaveField('finishDate', selected ? selected.toISOString().split('T')[0] : null)
                      }
                    }}
                    formatPicker={FORMAT_DATE}
                  />
                </div>
              ) : (
                <div>
                  <DetailItem
                    field={fieldValueArray[2].field}
                    fieldValue={isValidDate(dateEnd) ? dateEnd.toLocaleDateString() : t('digitalVaultDocumentView.noDate')}
                  />
                </div>
              )}
            </div>

            {/* Checkbox notify */}
            <div className='right-checbox'>
              <CheckBoxHLPure
                labelCheckBox={t('digitalVaultDocumentView.notifyExpiration')}
                checkedState={notifyExpiration}
                onChangeHandler={(e) => {
                  const checked = e.target.checked
                  setNotifyExpiration(checked)
                  if (!isEditing) {
                    return
                  }
                }}
                disabled={!isEditing}
              />
            </div>
          </div>
        </div>

        {/* --- Columna 2 --- */}
        <div className='documentDetailDataPure-section-div'>
          {isEditing ? (
            <>
              <div className='full-span' ref={ownerEditRef}>
                <DetailItem field={t('digitalVaultDocumentView.owner.document')} />
                <DropDownBtnHLPure
                  optionsDropDown={users}
                  optionsValue={uploadData.ownerId}
                  valueItem='id'
                  nameOption='fullName'
                  onDropDownChange={(e) => {
                    const ownerId = e.value ?? e.target?.value ?? e.target?.props?.id ?? null
                    setUploadData(prev => ({ ...prev, ownerId }))
                    if (!isEditing) {
                      handleSaveField('ownerId', ownerId)
                    }
                  }}
                />
              </div>
            </>
          ) : (
            <div>
              <DetailItem
                field={t('digitalVaultDocumentView.owner.document')}
                fieldValue={
                  users.find(u => String(u.id) === String(uploadData.ownerId))?.fullName
                  || documentData?.nameUser
                  || 'Sin propietario'
                }
              />
            </div>
          )}

          <div style={nameValue && nameValue.length > 100 ? { marginTop: '20px' } : {}}>
            <DetailItem field={fieldValueArray[4].field} fieldValue={fieldValueArray[4].value} />
          </div>

          <CheckBoxHLPure
            labelCheckBox={t('digitalVaultDocumentView.confidential')}
            checkedState={confidential}
            onChangeHandler={(e) => {
              const checked = e.target.checked
              setConfidential(checked)
              if (!isEditing) {
                return
              }
            }}
            disabled={!isEditing}
          />
        </div>

        {/* --- Columna 3: Descripción + Tags --- */}
        <div className='documentDetailDataPure-section-div'>
          {isEditing ? (
            <div className='full-span' ref={descriptionEditRef}>
              <TruncatedDetailItem field={descriptionField} />
              <TextAreaHLPure
                valueTextData={descriptionValue}
                handleChangeTextArea={(e) => setDescriptionValue(e.target.value)}
                onBlur={() => {
                  if (!isEditing) {
                    handleSaveField('description', descriptionValue)
                  }
                }}
                rowsTextArea={4}
                characterLimit={1000}
              />
            </div>
          ) : (
            <div>
              <TruncatedDetailItem
                field={descriptionField}
                value={descriptionValue}
                maxChars={MAX_TEXT_CHARS}
                onSeeMore={() => setOpenModal(true)}
                extraClass='description-value'

              />
            </div>
          )}

          <div className='detail-item-main-div'>
            {
              isEditing ? (
                <div
                  className='full-span'
                  ref={tagsEditRef}
                  tabIndex={-1}
                  onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null) }}
                >

                  <p className='detail-item-field'>
                    {fieldValueArray[7]?.field || t('digitalVaultFollowUpDocument.tags')}
                  </p>

                  <InputHLPure
                    typeInput='text'
                    valueInput={inputTag}
                    handleChangeInput={(e) => handleInputTag(e)}
                    inputActive={true}
                    onBlur={onBlurInputTag}
                    onKeyDown={(e) => onEnterTag(e)}
                    disabled={false}
                    characterLimit={50}
                  />

                  <div className='digitalVaultFollowUpDocument-followUpDocumentBasicData-tags'>
                    <ChipList
                      selection='single'
                      data={tags}
                      textField='text'
                      chip={(props) => (
                        <Chip
                          removable={true}
                          {...props}
                          onRemove={(e) => handleDeleteChipTag(e)}
                        />
                      )}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <p className='detail-item-field'>
                    {fieldValueArray[7]?.field || t('digitalVaultFollowUpDocument.tags')}
                  </p>
                  <div
                    className='tags-readonly'
                    role='button'
                    tabIndex={0}
                  >
                    {Array.isArray(tags) && tags.length > 0 ? (
                      tags.map((tag) => {
                        const key = tag?.value ?? tag?.text ?? String(Math.random())
                        const label = typeof tag === 'string' ? tag : (tag?.text || '')
                        return (
                          <span className='tag-pill' key={key}>#{label}</span>
                        )
                      })
                    ) : (
                      <span className='tag-empty'></span>
                    )}
                  </div>
                </>
              )
            }
          </div>
        </div>

        {/* --- Columna 4: Resumen --- */}
        {showSummary && (
          <div className='documentDetailDataPure-section-div'>
            <TruncatedDetailItem
              field={aiSummaryItem?.field || t('digitalVaultDocumentView.summary.ia')}
              value={summaryValue}
              maxChars={MAX_TEXT_CHARS}
              onSeeMore={() => setOpenModal(true)}
            />
            {isEditing && (
              <div style={{ marginTop: '8px' }}>
                <ButtonAddHLPure
                  className='buttonAddHLPure-add'
                  onClickBtn={(e) => { e.stopPropagation(); mergeAndSave() }}
                  nameBtn={t('digitalVaultDocumentView.merge')}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* --- Modal --- */}
      {openModal && (
        <InteractiveDialog
          title={t('digitalVaultDocumentView.descriptionAndSummary')}
          onClose={handleCloseModal}
          onSave={handleSaveModal}
          saveBtnTitle={tempDescription !== descriptionValue ? t('button.save') : null}
          cancelBtnTitle={t('button.return')}
          CustomButtons={
            <>
              <ButtonHLPure
                nameBtn={t('button.return')}
                onClickBtn={handleCloseModal}
              />
              <div style={{ flex: 1 }} />
              {tempDescription !== descriptionValue && (
                <ButtonHLPure
                  nameBtn={t('button.save')}
                  primary
                  onClickBtn={handleSaveModal}
                />
              )}
              {showSummary && (
                <ButtonHLPure
                  primary
                  nameBtn={t('digitalVaultDocumentView.merge')}
                  onClickBtn={mergeAndSave}
                />
              )}
            </>
          }
        >
          <TextAreaHLPure
            labelTextArea={t('digitalVaultDocumentView.description')}
            valueTextData={tempDescription}
            handleChangeTextArea={(e) => setTempDescription(e.target.value)}
            rowsTextArea={4}
            characterLimit={1000}
            readOnly={true}
          />

          {showSummary && (
            <div style={{ marginTop: '20px' }}>
              <TextAreaHLPure
                labelTextArea={t('digitalVaultDocumentView.summary.ia')}
                valueTextData={summaryValue}
                handleChangeTextArea={() => { }}
                rowsTextArea={4}
                characterLimit={1000}
                readOnly={true}
              />
            </div>
          )}
        </InteractiveDialog>
      )}

      <DialogSystemGLPure
        showWarningRequesting={showWarn}
        toggleWarningRequesting={toggleWarning}
        messageWarning={warnMsg}
        showButton
        messageButton={t('button.accept')}
        message={warnMsg}
      />
    </>
  )
})

export default DocumentDetailDataPure